// ─────────────────────────────────────────────────────────
// services/idb.ts — IndexedDB data store
//
// Gold Standard Migration: internals replaced with Dexie.
// All exported function names and signatures are unchanged —
// callers (idbSync.ts, repository.ts, auth.ts) need zero edits.
//
// Database : cbd_data  (Dexie, version 2)
// Version  : 2 (upgraded from 1 — adds write_queue table)
//
// Object stores — one per collection, all keyed by 'id':
//   proj, boq, milestones, daily, tender, emd, recv, ret,
//   exp, cf, petty, recon, vendor, po, quotations, invoice,
//   emp, adv, ins, tds, einvoices, task, audit, notif,
//   attendance  (id = monthKey e.g. "2024-3")
//   salaries    (id = sk      e.g. "2024-3-emp001")
//   brand       (id = 'config')
//   meta        (id = 'info'  — stores companyId, lastSyncAt)
// ─────────────────────────────────────────────────────────
import { dexieDb } from './dexieClient'

// All collection store names (unchanged — used by idbSync.ts + idbClearAll)
export const IDB_COLLECTIONS = [
  'proj', 'boq', 'milestones', 'daily', 'tender', 'emd',
  'recv', 'ret', 'exp', 'cf', 'petty', 'recon', 'vendor',
  'po', 'quotations', 'invoice', 'emp', 'adv', 'ins', 'tds',
  'einvoices', 'task', 'audit', 'notif',
  'attendance', 'salaries', 'brand', 'meta',
] as const

export type IDBCollection = typeof IDB_COLLECTIONS[number]

// ── Read all records from a store ─────────────────────────
export async function idbGetAll<T>(store: IDBCollection): Promise<T[]> {
  try {
    return (await (dexieDb[store] as import('dexie').Table<T>).toArray()) ?? []
  } catch (e) {
    console.warn(`[idb] getAll ${store} failed:`, e)
    return []
  }
}

// ── Upsert a single record (must have an 'id' field) ──────
export async function idbPut(
  store: IDBCollection,
  record: object & { id: string }
): Promise<void> {
  try {
    await (dexieDb[store] as import('dexie').Table).put(record)
  } catch (e) {
    console.warn(`[idb] put ${store} failed:`, e)
  }
}

// ── Delete a single record by id ──────────────────────────
export async function idbDelete(store: IDBCollection, id: string): Promise<void> {
  try {
    await (dexieDb[store] as import('dexie').Table).delete(id)
  } catch (e) {
    console.warn(`[idb] delete ${store}/${id} failed:`, e)
  }
}

// ── Overwrite all records in a store with a new array ─────
export async function idbReplaceAll(
  store: IDBCollection,
  records: Array<object & { id: string }>
): Promise<void> {
  try {
    const tbl = dexieDb[store] as import('dexie').Table
    await dexieDb.transaction('rw', tbl, async () => {
      await tbl.clear()
      await tbl.bulkPut(records)
    })
  } catch (e) {
    console.warn(`[idb] replaceAll ${store} failed:`, e)
  }
}

// ── Clear all records from a store ────────────────────────
export async function idbClear(store: IDBCollection): Promise<void> {
  try {
    await (dexieDb[store] as import('dexie').Table).clear()
  } catch (e) {
    console.warn(`[idb] clear ${store} failed:`, e)
  }
}

// ── Clear ALL data stores (called on logout) ──────────────
// Does NOT clear write_queue — that belongs to offline.ts.
export async function idbClearAll(): Promise<void> {
  try {
    const tables = IDB_COLLECTIONS.map(col => dexieDb[col] as import('dexie').Table)
    await dexieDb.transaction('rw', tables, async () => {
      for (const col of IDB_COLLECTIONS) {
        await (dexieDb[col] as import('dexie').Table).clear()
      }
    })
  } catch (e) {
    console.warn('[idb] clearAll failed:', e)
  }
}

// ── Meta record (companyId + sync timestamp) ──────────────
export interface IDBMeta {
  id:         'info'
  companyId:  string
  lastSyncAt: string | null
}

export async function idbGetMeta(): Promise<IDBMeta | null> {
  try {
    return (await dexieDb.meta.get('info') as IDBMeta | undefined) ?? null
  } catch {
    return null
  }
}

export async function idbSetMeta(meta: Omit<IDBMeta, 'id'>): Promise<void> {
  await idbPut('meta', { id: 'info', ...meta })
}
